import { NoPhotography } from '@mui/icons-material'
import { ButtonBase, Card, CircularProgress, Grid, Theme, Typography } from '@mui/material'
import * as controllers from 'controllers'
import { VehicleCheckInPhotoType } from 'dtos'
import { useEffect, useRef, useState } from 'react'

export type VehiclePhotoType =
  | VehicleCheckInPhotoType
  | 'pulled-cluster'
  | 'third-party-conversion-delivery'
  | 'odometer-after-modification'

type VehiclePhotoSource = 'vehicle-check-in' | 'cluster-reinstall' | 'cluster-photos'

interface VehiclePhotoCardProps {
  complete?: boolean
  disabled?: boolean
  documentGuid?: string
  displayFileName?: string
  fileName?: string
  imageHeight?: number
  imageWidth?: number
  label?: string
  lazyThumbnail?: boolean
  onClick?: () => void
  selected?: boolean
  showPlaceholderImage?: boolean
  source?: VehiclePhotoSource
  type?: VehiclePhotoType
  vehicleGuid?: string
  clusterShipperGuid?: string
  width?: string
  warning?: boolean
}

export default function VehiclePhotoCard({
  complete,
  disabled,
  documentGuid,
  displayFileName,
  fileName,
  imageHeight = 112.5,
  imageWidth = 200,
  label,
  lazyThumbnail = false,
  onClick,
  selected,
  showPlaceholderImage = false,
  source = 'vehicle-check-in',
  type,
  vehicleGuid,
  clusterShipperGuid,
  width = '220px',
  warning
}: VehiclePhotoCardProps) {
  const rootRef = useRef<HTMLButtonElement | null>(null)
  const [isInView, setIsInView] = useState(!lazyThumbnail)
  const [isLoading, setIsLoading] = useState(false)
  const [photoFileBase64, setPhotoFileBase64] = useState('')
  const [format, setFormat] = useState('')

  useEffect(() => {
    if (!lazyThumbnail) {
      return
    }

    const element = rootRef.current
    if (!element) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '160px' }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [lazyThumbnail])

  useEffect(() => {
    if (!isInView) {
      return
    }

    if ((!documentGuid && !fileName) || !vehicleGuid) {
      setPhotoFileBase64('')
      return
    }

    setFormat((displayFileName ?? fileName ?? '').split('.').pop() ?? 'jpeg')
    setIsLoading(true)

    let base64Promise: Promise<string>
    const photoDocumentGuid = documentGuid ?? fileName
    if (photoDocumentGuid) {
      if (source === 'cluster-reinstall') {
        base64Promise = controllers.getNeedsClusterReinstalledPhotoBase64(vehicleGuid, photoDocumentGuid)
      } else if (source === 'cluster-photos' && type === 'pulled-cluster') {
        base64Promise = controllers.getNeedsClusterRemovedPhotoBase64(vehicleGuid, photoDocumentGuid)
      } else if (source === 'cluster-photos' && type === 'third-party-conversion-delivery') {
        base64Promise = clusterShipperGuid
          ? controllers.getClusterShipperThirdPartyConversionDeliveryPhotoBase64(clusterShipperGuid, fileName ?? '')
          : Promise.resolve('')
      } else if (
        source === 'cluster-photos' &&
        ['cluster-with-odometer', 'importer-door-label', 'combo', 'odometer-after-modification'].includes(type ?? '')
      ) {
        base64Promise = controllers.getNeedsClusterReinstalledPhotoBase64(vehicleGuid, photoDocumentGuid)
      } else if (source === 'vehicle-check-in') {
        base64Promise = controllers.getVehicleCheckInPhotoBase64(vehicleGuid, photoDocumentGuid)
      } else {
        base64Promise = controllers.getVehicleDocumentBase64(vehicleGuid, photoDocumentGuid)
      }
    } else {
      base64Promise = Promise.resolve('')
    }

    base64Promise
      .then(base64 => {
        setPhotoFileBase64(base64)
      })
      .catch(() => {
        setPhotoFileBase64('')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [clusterShipperGuid, displayFileName, documentGuid, fileName, isInView, source, type, vehicleGuid])

  const getColor = (theme: Theme): string => {
    if (selected && complete) {
      return theme.palette.success.light
    }

    if (selected && (warning || disabled)) {
      return warning ? theme.palette.warning.light : theme.palette.grey[300]
    }

    if (disabled) {
      return theme.palette.grey[400]
    }

    if (complete) {
      return theme.palette.success.main
    }

    if (warning) {
      return theme.palette.warning.main
    }

    return theme.palette.error.main
  }

  const getPlaceholderImage = (): string => {
    switch (type) {
      case 'manufacturers-label':
        return '/1 Manufacturers Label.jpg'
      case 'tire-pressure-label':
        return '/2 Tire Pressure Label.jpg'
      case 'importer-door-label':
        return '/3 Importer Door Label.jpg'
      case 'combo':
        return '/4 Combo.jpg'
      case 'check-in-cluster':
        return '/5 Check In Cluster.jpg'
      case 'cluster-with-odometer':
        return '/6 Cluster with Odometer.jpg'
      case 'tpms':
        return '/7 TPMS.jpg'
      case 'driver-airbag':
        return '/8 Driver Airbag.jpg'
      case 'backup-cam':
        return '/9 Backup Cam.jpg'
      case 'airbag-tell-tale':
        return '/10 Airbag tell-tale.jpg'
      case 'passenger-airbag':
        return '/11 Passenger Airbag.jpg'
      case 'vin-on-dashboard':
        return '/12 VIN on Dashboard.jpg'
      case 'roof':
        return '/13 Roof.jpg'
      case 'front-corner':
        return '/14 Front corner.jpg'
      case 'front-head-on':
        return '/15 Front head on.jpg'
      case 'rear-corner':
        return '/16 Rear corner.jpg'
      case 'rear-head-on':
        return '/17 Rear head on.jpg'
      default:
        return ''
    }
  }

  const getLabel = (): string | undefined => {
    if (label) {
      return label
    }

    switch (type) {
      case 'manufacturers-label':
        return "Manufacturer's Label"
      case 'tire-pressure-label':
        return 'Tire Pressure Label'
      case 'importer-door-label':
        return 'Importer Door Label'
      case 'combo':
        return 'Combo'
      case 'check-in-cluster':
        return 'Speedometer Before Modification'
      case 'cluster-with-odometer':
        return source === 'vehicle-check-in' ? 'Odometer Before Modification' : 'Speedometer After Modification'
      case 'tpms':
        return 'TPMS'
      case 'driver-airbag':
        return 'Driver Airbag'
      case 'backup-cam':
        return 'Backup Cam'
      case 'airbag-tell-tale':
        return 'Airbag Tell-Tale'
      case 'passenger-airbag':
        return 'Passenger Airbag'
      case 'vin-on-dashboard':
        return 'VIN on Dashboard'
      case 'roof':
        return 'Roof'
      case 'front-corner':
        return 'Front Corner'
      case 'front-head-on':
        return 'Front Head On'
      case 'rear-corner':
        return 'Rear Corner'
      case 'rear-head-on':
        return 'Rear Head On'
      case 'pulled-cluster':
        return 'Pulled Cluster'
      case 'third-party-conversion-delivery':
        return '3rd Party Conversion Delivery'
      case 'odometer-after-modification':
        return 'Odometer After Modification'
      default:
        return undefined
    }
  }

  const placeholderImage = getPlaceholderImage()
  const imageContent = isInView && isLoading ? (
    <CircularProgress sx={{ color: '#fff', mt: 5 }} />
  ) : isInView && photoFileBase64 ? (
    <img alt={type} src={`data:image/${format};base64,${photoFileBase64}`} width={imageWidth} id={fileName} height={imageHeight} />
  ) : showPlaceholderImage && placeholderImage ? (
    <img alt={type} src={placeholderImage} width={imageWidth} height={imageHeight} style={{ objectFit: 'cover' }} />
  ) : (
    <NoPhotography fontSize='large' sx={{ height: imageHeight }} />
  )

  return (
    <Card
      ref={rootRef}
      component={ButtonBase}
      onClick={onClick}
      sx={theme => ({
        backgroundColor: getColor(theme),
        color: '#fff',
        p: 1,
        width,
        height: '176px',
        maxHeight: '176px'
      })}
    >
      <Grid
        container
        direction='column'
        wrap='nowrap'
        spacing={0}
        alignItems='center'
        justifyContent='center'
        sx={{ height: '100%', width: '100%' }}
      >
        <Grid item sx={{ minHeight: imageHeight, maxHeight: imageHeight, flexShrink: 0 }}>
          {imageContent}
        </Grid>

        <Grid item xs={12} sx={{ width: '100%', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography align='center' variant='body2'>
            {getLabel()}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  )
}
